import { gql } from "@apollo/client";

export const TRAINER_AFFILIATES = gql`
  query TrainerAffiliates(
    $limit: Int!
    $offset: Int!
    $search: String!
    $statusPattern: String!
    $activeStates: [Boolean!]!
  ) {
    trainer_affiliates(
      limit: $limit
      offset: $offset
      order_by: { updated_at: desc }
      where: {
        _and: [
          { is_active: { _in: $activeStates } }
          { affiliate: { status: { _ilike: $statusPattern } } }
          {
            _or: [
              { special_trainer: { trainer_name: { _ilike: $search } } }
              { special_trainer: { trainer_secondary_text: { _ilike: $search } } }
              { affiliate: { affiliate_code: { _ilike: $search } } }
              { affiliate: { user: { username: { _ilike: $search } } } }
            ]
          }
        ]
      }
    ) {
      id
      is_active
      total_referrals
      total_points
      created_at
      updated_at
      special_trainer {
        id
        trainer_name
        trainer_secondary_text
      }
      affiliate {
        id
        affiliate_code
        status
        reward_type
        points_per_referral
        total_points_earned
        pending_points
        redeemed_points
        user {
          username
          phone
        }
        affiliate_referrals_aggregate {
          aggregate {
            count
          }
        }
      }
    }
    trainer_affiliates_aggregate(
      where: {
        _and: [
          { is_active: { _in: $activeStates } }
          { affiliate: { status: { _ilike: $statusPattern } } }
          {
            _or: [
              { special_trainer: { trainer_name: { _ilike: $search } } }
              { special_trainer: { trainer_secondary_text: { _ilike: $search } } }
              { affiliate: { affiliate_code: { _ilike: $search } } }
              { affiliate: { user: { username: { _ilike: $search } } } }
            ]
          }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const TRAINER_AFFILIATE_DETAIL = gql`
  query TrainerAffiliateDetail($id: uuid!) {
    trainer_affiliates_by_pk(id: $id) {
      id
      is_active
      total_referrals
      total_points
      created_at
      updated_at
      special_trainer {
        id
        trainer_name
        trainer_secondary_text
      }
      affiliate {
        id
        affiliate_code
        status
        reward_type
        points_per_referral
        total_points_earned
        pending_points
        redeemed_points
        user {
          username
          phone
        }
        affiliate_commissions(
          order_by: { created_at: desc }
          where: { fk_transaction_id: { _is_null: false } }
          limit: 25
        ) {
          id
          status
          points_awarded
          reward_type
          created_at
          transaction {
            id
            amount
            status
            user {
              username
              phone
            }
          }
        }
        affiliate_referrals(order_by: { referred_at: desc }) {
          id
          referred_at
          first_order_completed
          user {
            username
            phone
          }
        }
        affiliate_referrals_aggregate {
          aggregate {
            count
          }
        }
        affiliate_point_redemptions(order_by: { requested_at: desc }) {
          id
          status
          points_redeemed
          redemption_type
          redemption_value
          payment_reference
          requested_at
          processed_at
          completed_at
        }
      }
    }
  }
`;

export const TRAINER_AFFILIATE_COMMISSIONS_GROUPED = gql`
  query TrainerAffiliateCommissionsGrouped(
    $fk_affiliate_id: uuid!
    $startDate: timestamptz
    $endDate: timestamptz
  ) {
    affiliate_commissions(
      where: {
        fk_affiliate_id: { _eq: $fk_affiliate_id }
        fk_transaction_id: { _is_null: false }
        created_at: { 
          _gte: $startDate
          _lte: $endDate
        }
      }
      order_by: { created_at: desc }
    ) {
      id
      points_awarded
      status
      created_at
      transaction {
        id
        amount
        user {
          id
          username
          phone
        }
      }
    }
  }
`;

export const TRAINER_AFFILIATE_REFERRALS = gql`
  query TrainerAffiliateReferrals(
    $fk_affiliate_id: uuid!
    $startDate: timestamptz
    $endDate: timestamptz
    $limit: Int!
    $offset: Int!
  ) {
    affiliate_referrals(
      where: {
        fk_affiliate_id: { _eq: $fk_affiliate_id }
        referred_at: { _gte: $startDate, _lte: $endDate }
      }
      order_by: { referred_at: desc }
      limit: $limit
      offset: $offset
    ) {
      id
      referred_at
      first_order_completed
      user {
        username
        phone
      }
    }
    affiliate_referrals_aggregate(
      where: {
        fk_affiliate_id: { _eq: $fk_affiliate_id }
        referred_at: { _gte: $startDate, _lte: $endDate }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const TRAINER_AFFILIATE_REDEMPTIONS = gql`
  query TrainerAffiliateRedemptions(
    $fk_affiliate_id: uuid!
    $startDate: timestamptz
    $endDate: timestamptz
    $limit: Int!
    $offset: Int!
  ) {
    affiliate_point_redemptions(
      where: {
        fk_affiliate_id: { _eq: $fk_affiliate_id }
        requested_at: { _gte: $startDate, _lte: $endDate }
      }
      order_by: { requested_at: desc }
      limit: $limit
      offset: $offset
    ) {
      id
      status
      points_redeemed
      redemption_type
      redemption_value
      payment_reference
      requested_at
      processed_at
      completed_at
    }
    affiliate_point_redemptions_aggregate(
      where: {
        fk_affiliate_id: { _eq: $fk_affiliate_id }
        requested_at: { _gte: $startDate, _lte: $endDate }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const UPDATE_AFFILIATE_STATUS = gql`
  mutation UpdateAffiliateStatus($id: uuid!, $status: String!) {
    update_affiliates_by_pk(pk_columns: { id: $id }, _set: { status: $status }) {
      id
      status
    }
  }
`;

export const UPDATE_TRAINER_AFFILIATE_ACTIVE = gql`
  mutation UpdateTrainerAffiliate($id: uuid!, $is_active: Boolean!) {
    update_trainer_affiliates_by_pk(
      pk_columns: { id: $id }
      _set: { is_active: $is_active }
    ) {
      id
      is_active
    }
  }
`;

export const AFFILIATE_COMMISSIONS = gql`
  query AffiliateCommissions(
    $limit: Int!
    $offset: Int!
    $statusPattern: String!
  ) {
    affiliate_commissions(
      limit: $limit
      offset: $offset
      order_by: { created_at: desc }
      where: {
        reward_type: { _eq: "POINTS" }
        fk_transaction_id: { _is_null: false }
        status: { _ilike: $statusPattern }
      }
    ) {
      id
      fk_affiliate_id
      status
      points_awarded
      created_at
      fk_transaction_id
      affiliate {
        id
        affiliate_code
        user {
          username
          phone
        }
        trainer_affiliate {
          special_trainer {
            trainer_name
            trainer_secondary_text
          }
        }
      }
      transaction {
        id
        amount
        status
        user {
          username
          phone
        }
      }
    }
    affiliate_commissions_aggregate(
      where: {
        reward_type: { _eq: "POINTS" }
        fk_transaction_id: { _is_null: false }
        status: { _ilike: $statusPattern }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const APPROVE_COMMISSION = gql`
  mutation ApproveCommission(
    $id: uuid!
    $fk_affiliate_id: uuid!
    $points_awarded: Int!
  ) {
    update_affiliate_commissions_by_pk(
      pk_columns: { id: $id }
      _set: { status: "APPROVED" }
    ) {
      id
      status
    }
    update_affiliates_by_pk(
      pk_columns: { id: $fk_affiliate_id }
      _inc: { 
        pending_points: $points_awarded
      }
    ) {
      id
      pending_points
      total_points_earned
    }
  }
`;

export const REJECT_COMMISSION = gql`
  mutation RejectCommission($id: uuid!) {
    update_affiliate_commissions_by_pk(
      pk_columns: { id: $id }
      _set: { status: "CANCELLED" }
    ) {
      id
      status
    }
  }
`;

export const AFFILIATE_REDEMPTIONS = gql`
  query AffiliateRedemptions(
    $limit: Int!
    $offset: Int!
    $statusPattern: String!
  ) {
    affiliate_point_redemptions(
      limit: $limit
      offset: $offset
      order_by: { requested_at: desc }
      where: { status: { _ilike: $statusPattern } }
    ) {
      id
      fk_affiliate_id
      status
      points_redeemed
      redemption_type
      redemption_value
      payment_reference
      requested_at
      processed_at
      completed_at
      affiliate {
        id
        affiliate_code
        user {
          username
          phone
        }
        trainer_affiliate {
          special_trainer {
            trainer_name
            trainer_secondary_text
          }
        }
      }
    }
    affiliate_point_redemptions_aggregate(
      where: { status: { _ilike: $statusPattern } }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const UPDATE_REDEMPTION_STATUS = gql`
  mutation UpdateRedemptionStatus(
    $id: uuid!
    $status: String!
    $payment_reference: String
  ) {
    update_affiliate_point_redemptions_by_pk(
      pk_columns: { id: $id }
      _set: { status: $status, payment_reference: $payment_reference }
    ) {
      id
      status
      payment_reference
    }
  }
`;

export const CREATE_REDEMPTION_REQUEST = gql`
  mutation CreateRedemptionRequest(
    $fk_affiliate_id: uuid!
    $points_redeemed: Int!
    $redemption_type: String!
    $redemption_value: numeric
    $payment_method: String
    $payment_reference: String
    $notes: String
  ) {
    insert_affiliate_point_redemptions_one(
      object: {
        fk_affiliate_id: $fk_affiliate_id
        points_redeemed: $points_redeemed
        redemption_type: $redemption_type
        redemption_value: $redemption_value
        payment_method: $payment_method
        payment_reference: $payment_reference
        notes: $notes
        status: "PENDING"
      }
    ) {
      id
      points_redeemed
      status
      redemption_type
      redemption_value
      created_at
    }
  }
`;

export const COMPLETE_REDEMPTION = gql`
  mutation CompleteRedemption(
    $id: uuid!
    $payment_reference: String!
    $payment_method: String
    $notes: String
    $fk_affiliate_id: uuid!
    $points_redeemed: Int!
  ) {
    update_affiliate_point_redemptions_by_pk(
      pk_columns: { id: $id }
      _set: { 
        status: "COMPLETED"
        payment_reference: $payment_reference
        payment_method: $payment_method
        notes: $notes
        completed_at: "now()"
      }
    ) {
      id
      status
      payment_reference
      payment_method
      completed_at
    }
    update_affiliates_by_pk(
      pk_columns: { id: $fk_affiliate_id }
      _inc: { redeemed_points: $points_redeemed }
    ) {
      id
      redeemed_points
    }
  }
`;

export const APPROVE_REDEMPTION = gql`
  mutation ApproveRedemption(
    $id: uuid!
    $fk_affiliate_id: uuid!
    $points_redeemed: Int!
  ) {
    update_affiliate_point_redemptions_by_pk(
      pk_columns: { id: $id }
      _set: { 
        status: "APPROVED"
        processed_at: "now()"
      }
    ) {
      id
      status
      processed_at
    }
    update_affiliates_by_pk(
      pk_columns: { id: $fk_affiliate_id }
      _inc: { redeemed_points: $points_redeemed }
    ) {
      id
      redeemed_points
    }
  }
`;

export const REJECT_REDEMPTION = gql`
  mutation RejectRedemption(
    $id: uuid!
    $notes: String
  ) {
    update_affiliate_point_redemptions_by_pk(
      pk_columns: { id: $id }
      _set: { 
        status: "REJECTED"
        notes: $notes
      }
    ) {
      id
      status
      notes
    }
  }
`;

export const TRAINER_TRANSACTIONS = gql`
  query TrainerTransactions(
    $limit: Int!
    $offset: Int!
    $search: String!
    $statusPattern: String!
    $createdAtGte: timestamptz!
    $createdAtLte: timestamptz!
  ) {
    transactions(
      limit: $limit
      offset: $offset
      order_by: { created_at: desc }
      where: {
        fk_affiliate_id: { _is_null: false }
        status: { _ilike: $statusPattern }
        created_at: { _gte: $createdAtGte, _lte: $createdAtLte }
        _or: [
          { user: { username: { _ilike: $search } } }
          { affiliate: { affiliate_code: { _ilike: $search } } }
          { affiliate: { user: { username: { _ilike: $search } } } }
        ]
      }
    ) {
      id
      amount
      status
      created_at
      transaction_type
      purchase_type
      fk_affiliate_id
      affiliate {
        id
        affiliate_code
        user {
          username
        }
        trainer_affiliate {
          special_trainer {
            trainer_name
            trainer_secondary_text
          }
        }
      }
      user {
        id
        username
        phone
      }
      special_package_plan_image {
        id
        image_url
      }
    }
    transactions_aggregate(
      where: {
        fk_affiliate_id: { _is_null: false }
        status: { _ilike: $statusPattern }
        created_at: { _gte: $createdAtGte, _lte: $createdAtLte }
        _or: [
          { user: { username: { _ilike: $search } } }
          { affiliate: { affiliate_code: { _ilike: $search } } }
          { affiliate: { user: { username: { _ilike: $search } } } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const TRAINER_TRANSACTIONS_BY_USER = gql`
  query TrainerTransactionsByUser(
    $user_id: uuid!
    $limit: Int!
    $offset: Int!
    $statusPattern: String!
  ) {
    transactions(
      limit: $limit
      offset: $offset
      order_by: { created_at: desc }
      where: {
        fk_affiliate_id: { _is_null: false }
        status: { _ilike: $statusPattern }
        user: { id: { _eq: $user_id } }
      }
    ) {
      id
      amount
      status
      created_at
      transaction_type
      purchase_type
      fk_affiliate_id
      affiliate {
        id
        affiliate_code
        trainer_affiliate {
          special_trainer {
            trainer_name
            trainer_secondary_text
          }
        }
      }
      user {
        id
        username
        phone
      }
      special_package_plan_image {
        id
        image_url
      }
    }
    transactions_aggregate(
      where: {
        fk_affiliate_id: { _is_null: false }
        status: { _ilike: $statusPattern }
        user: { id: { _eq: $user_id } }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const CREATE_TRAINER_AFFILIATE_DIRECT = gql`
  mutation CreateTrainerAffiliateDirect(
    $trainer_id: uuid!
    $user_id: uuid!
    $code: String!
    $points: Int!
  ) {
    insert_affiliates_one(
      object: {
        fk_user_id: $user_id
        affiliate_code: $code
        status: "ACTIVE"
        reward_type: "POINTS"
        referral_type: "CODE"
        points_per_referral: $points
        trainer_affiliate: {
          data: { fk_trainer_id: $trainer_id, is_active: true }
        }
      }
    ) {
      id
      affiliate_code
      trainer_affiliate {
        id
        is_active
      }
    }
  }
`;

